import * as React from 'react';
import UserChartNotificationSettingsInterface from '@app/Interface/UserChartNotificationSettingsInterface';
import User from '@app/Interface/UserInterface';
import { ActionGroup, Alert, Button, Card, CardBody, Form, FormAlert, Grid, GridItem, PageSection, Select, SelectOption, SelectOptionObject, SelectVariant, Split, SplitItem, Switch, TextInput, Title } from '@patternfly/react-core';
import { UserContext } from '@app/Context/UserContext';
import ChartNotificationHelper from '@app/Service/ChartNotificationHelper';
import ChartSettingFunctions from '@app/Page/ChartSettingPage/ChartSettingFunctions';
import ThresholdType from '@app/Type/ThresholdType';
import UpdateChartNotificationInterface from '@app/Interface/UpdateChartNotificationInterface';
import { ToastNotificationType } from '@app/Interface/ToastNotificationInterface';
import { CreateToastNotification, ToastNotificationContext } from '@app/Context/ToastNotificationContext';
import NDALineChart from '@app/Components/Chart/NDALineChart';
import ChartDataHelper from '@app/Service/ChartDataHelper';
import ChartAverageInterface from '@app/Interface/Chart/ChartAverageInterface';
import PageLoader from '@app/Page/PageLoader';
import PageInterface from '@app/Interface/PageInterface';

interface ChartSettingEditProps {
    currentUser: User;
    setting: UserChartNotificationSettingsInterface;
    recommendedSetting: UserChartNotificationSettingsInterface;
    handleBackButton: () => void;
    chartPage: PageInterface | null;
}

const ChartSettingEdit: React.FC<ChartSettingEditProps> = ({ currentUser, setting, recommendedSetting, handleBackButton, chartPage }) => {

    // NOTE: ToastNotificationContext provider was added to `src\app\index.tsx`
    const { toastDispatch } = React.useContext(ToastNotificationContext);

    const [formSettings, setFormSettings] = React.useState<UserChartNotificationSettingsInterface>(setting);

    const [isThresholdOpen, setIsThresholdOpen] = React.useState(false);

    const initialShowMaxField = (setting.threshold_range === "between" || setting.threshold_range === "outside") ? true : false;

    const [showMaxField, setShowMaxField] = React.useState(initialShowMaxField);

    const { userDispatch } = React.useContext(UserContext);

    const defaultFormValidated = "default";

    let defaultThresholdRange = setting.threshold_range;

    let isThresholdSelectDisabled = false;

    // DNDA-1383 check for guardrail
    if (recommendedSetting.enable_guardrail && recommendedSetting.guardrail_threshold) {
        defaultThresholdRange = recommendedSetting.guardrail_threshold;
        isThresholdSelectDisabled = true;
    }

    const [selectedThreshold, setSelectedThreshold] = React.useState<ThresholdType>(defaultThresholdRange);

    // for values: see https://www.patternfly.org/v4/components/form#formgroup
    const [requiredValidated, setRequiredValidated] = React.useState(defaultFormValidated);

    const [validationMessage, setValidationMessage] = React.useState("Wrong input values.");

    const [isFormProcessing, setIsFormProcessing] = React.useState(false);

    const [chartData, setChartData] = React.useState<ChartAverageInterface[]>([]);

    const [chartDataLoading, setChartDataLoading] = React.useState(true);

    // Load chart data on page load
    React.useEffect(() => {
        ChartDataHelper
            .getChartAveragesData(setting.chart, currentUser.currentFpuId, chartPage?.id ?? 0)
            .then(data => {
                setChartData(data);
            })
            .finally(() => setChartDataLoading(false));
    }, [chartPage?.id, currentUser.currentFpuId, setting.chart]);

    const renderChartComponent = () => {
        if (!formSettings.active) {
            return null;
        }

        if (chartDataLoading) {
            return <PageLoader />;
        }

        if (chartData.length === 0) {
            return (
                <p>
                    <em>No chart data to show for the selected producer</em>
                </p>
            );
        }

        return (
            <NDALineChart
                data={chartData}
                chartType={setting.chart}
                hasBaseline={!!chartPage?.acf?.chart_baseline && chartPage?.acf?.chart_baseline !== 0}
                baselineCaption={chartPage?.acf?.chart_baseline_caption ?? ''}
                hasNotificationThreshold={true}
                userNotificationThreshold={formSettings}
            />
        );
    };

    const handleChange = (newValue: string | boolean, e: React.FormEvent<HTMLInputElement>) => {

        const fieldId = e.currentTarget.id;

        const updatedSettings = { ...formSettings };

        switch (fieldId) {
            case "active":
                updatedSettings.active = typeof newValue === "boolean" ? newValue : false;
                break;

            case "threshold_value_min":

                // check for guardrail
                if (recommendedSetting.enable_guardrail) {

                    if (recommendedSetting.guardrail_threshold === "above" && Number(newValue) > recommendedSetting.guardrail_value) {
                        newValue = recommendedSetting.guardrail_value.toString();
                    } else if (recommendedSetting.guardrail_threshold === "below" && Number(newValue) < recommendedSetting.guardrail_value) {
                        newValue = recommendedSetting.guardrail_value.toString();
                    }

                }

                updatedSettings.threshold_value_min = Number(newValue);

                updatedSettings.threshold_display_value_min = newValue.toString();

                // if threshold is above or below, then max should be the same
                // NOTE: max value will not be available
                if (updatedSettings.threshold_range === "above" || updatedSettings.threshold_range === "below") {

                    updatedSettings.threshold_value_max = Number(newValue);

                    updatedSettings.threshold_display_value_max = newValue.toString();

                }

                break;

            case "threshold_value_max":

                // check for guardrail
                if (recommendedSetting.enable_guardrail) {

                    if (recommendedSetting.guardrail_threshold === "above" && Number(newValue) > recommendedSetting.guardrail_value) {
                        newValue = recommendedSetting.guardrail_value.toString();
                    } else if (recommendedSetting.guardrail_threshold === "below" && Number(newValue) < recommendedSetting.guardrail_value) {
                        newValue = recommendedSetting.guardrail_value.toString();
                    }

                }

                updatedSettings.threshold_value_max = Number(newValue);

                updatedSettings.threshold_display_value_max = newValue.toString();

                break;

            default:
                break;
        }

        setFormSettings(updatedSettings);

        // check to see if active state was changed 
        // if so, save ALL the data. 
        if (fieldId === "active") {

            const data = ChartSettingFunctions.getChartData(currentUser, updatedSettings);

            saveUserContext();

            saveData(data);
        }

    }

    const onSelectThresholdRange = (event: React.MouseEvent<Element, MouseEvent> | React.ChangeEvent<Element>, value: string | SelectOptionObject, isPlaceholder?: boolean | undefined) => {

        const updatedSettings = { ...formSettings };

        if (isPlaceholder) {

            setSelectedThreshold("above");

            setIsThresholdOpen(false);

            updatedSettings.threshold_range = "above"

            setFormSettings(updatedSettings);

            setShowMaxField(false);

        } else {

            let selection: ThresholdType = "above";

            if (typeof value === "string") {
                selection = ChartSettingFunctions.convertStringToThresholdType(value);

            }

            let showSecondField = false;

            if (selection === "between" || selection === "outside") {
                showSecondField = true;
            }

            setSelectedThreshold(selection);

            updatedSettings.threshold_range = selection;

            setFormSettings(updatedSettings);

            setIsThresholdOpen(!isThresholdOpen);

            setShowMaxField(showSecondField);

        }

    }

    const onThresholdToggle = (isOpen: boolean) => {

        setIsThresholdOpen(isOpen);

    }

    /**
     * Handler when switch is updated. 
     * 
     * @param checked 
     * @param event 
     */
    const handleSubmit = (event: React.SyntheticEvent) => {

        event.preventDefault();

        setIsFormProcessing(true);

        setRequiredValidated("default");

        let notificationType: ToastNotificationType = ToastNotificationType.Default;

        let toastTitle = "";

        let toastDescription = "";

        const [validForm, toastMessage] = ChartSettingFunctions.formValidation(formSettings);

        if (validForm) {

            const data = ChartSettingFunctions.getChartData(currentUser, formSettings);

            saveUserContext();

            saveData(data);

            notificationType = ToastNotificationType.Success;

            toastTitle = "Success";

            toastDescription = "Notification setting was successfully saved.";

        } else {

            setRequiredValidated("error");

            notificationType = ToastNotificationType.Danger;

            toastTitle = "Error";

            toastDescription = toastMessage;

            setValidationMessage(toastMessage);

        }

        CreateToastNotification(toastDispatch, notificationType, toastTitle, toastDescription);

        setIsFormProcessing(false);

    }

    const saveUserContext = () => {

        const updatedUser = { ...currentUser };

        const userSettings = { ...formSettings };

        let isNew = true;

        // find the matching setting and update it
        if (typeof updatedUser.fpuChartNotificationSettings !== "boolean") {

            updatedUser.fpuChartNotificationSettings.forEach((chartSetting, index) => {

                if (chartSetting.fpu === setting.fpu && chartSetting.chart === setting.chart) {

                    // replace the chart setting values
                    updatedUser.fpuChartNotificationSettings[index] = userSettings;

                    isNew = false;

                }

            });

        }

        // if isnew we need to ADD a new threshold to the user.
        if (isNew) {

            if (typeof updatedUser.fpuChartNotificationSettings === "boolean") {
                updatedUser.fpuChartNotificationSettings = [userSettings];
            } else {
                updatedUser.fpuChartNotificationSettings.push(userSettings);
            }

        }

        // update user context
        userDispatch({
            type: "UPDATE",
            payload: updatedUser
        });

    }

    const saveData = (data: UpdateChartNotificationInterface) => {

        // Save setting to middleware endpoint
        // send middleware request to update user. DON'T wait
        try {

            ChartNotificationHelper.updateChartNotification(data);

        } catch (error) {
            console.group("ChartSettingEdit -> saveData");
            console.info("error: ", error);
            console.groupEnd();
        }

    }

    const thresholdItems = [
        <SelectOption key={0} value="above">Higher</SelectOption>,
        <SelectOption key={1} value="below">Lower</SelectOption>,
        <SelectOption key={2} value="outside">Outside</SelectOption>,
        <SelectOption key={3} value="between">Between</SelectOption>,
    ];

    React.useEffect(() => {

        return () => {

            // on unmount, clear all toast messages
            toastDispatch({
                type: "CLEAR"
            });

        };

    }, [toastDispatch]);

    return (
        <PageSection>

            <Title headingLevel="h1" className="pf-u-mb-md">{setting.chart.toUpperCase()} Notifications</Title>

            <Grid
                hasGutter
                sm={12}
                md={6}
                lg={6}
                xl={6}
                xl2={6}
            >
                <GridItem>

                    <Form isWidthLimited onSubmit={handleSubmit}>

                        {requiredValidated === "error" && (
                            <FormAlert>
                                <Alert
                                    variant="danger"
                                    title={validationMessage}
                                    aria-live="polite"
                                    role="alert"
                                    isInline
                                ></Alert>
                            </FormAlert>
                        )}

                        <Card className="pf-u-mb-md">
                            <CardBody>
                                <Split hasGutter>
                                    <SplitItem isFilled>
                                        <Split hasGutter>
                                            <SplitItem>I want to receive this notification</SplitItem>
                                        </Split>
                                    </SplitItem>
                                    <SplitItem>
                                        <Switch
                                            id="active"
                                            aria-label={`Get Notifications`}
                                            isChecked={formSettings.active}
                                            onChange={handleChange}
                                        />
                                    </SplitItem>
                                </Split>
                            </CardBody>
                        </Card>

                        {!formSettings.active &&
                            <Button variant="link" isInline className="pf-u-pl-0" onClick={() => handleBackButton()}>Back</Button>
                        }

                        {formSettings.active &&

                            <>
                                <div>Notify when {setting.chart.toUpperCase()} is</div>

                                <Split hasGutter>
                                    <SplitItem>
                                        <Select
                                            id="threshold_range"
                                            variant={SelectVariant.single}
                                            aria-label="Select threshold type"
                                            onToggle={onThresholdToggle}
                                            onSelect={onSelectThresholdRange}
                                            selections={selectedThreshold}
                                            isOpen={isThresholdOpen}
                                            isDisabled={isThresholdSelectDisabled}
                                        >
                                            {thresholdItems}
                                        </Select>
                                    </SplitItem>

                                    {!showMaxField && <SplitItem className="pf-u-pt-sm">Than</SplitItem>}

                                    <SplitItem>
                                        <TextInput
                                            isRequired={true}
                                            type="number"
                                            id="threshold_value_min"
                                            name="threshold_value_min"
                                            value={formSettings.threshold_display_value_min}
                                            onChange={handleChange}
                                            step={.1}
                                            min={recommendedSetting.enable_guardrail && recommendedSetting.guardrail_threshold === "below" ? recommendedSetting.guardrail_value : 0}
                                            max={recommendedSetting.enable_guardrail && recommendedSetting.guardrail_threshold === "above" ? recommendedSetting.guardrail_value : undefined}
                                            aria-label="Value 1"
                                        />
                                    </SplitItem>

                                    {showMaxField &&
                                        <>
                                            <SplitItem className="pf-u-pt-sm">And</SplitItem>
                                            <SplitItem>
                                                <TextInput
                                                    isRequired={true}
                                                    type="number"
                                                    id="threshold_value_max"
                                                    name="threshold_value_max"
                                                    value={formSettings.threshold_display_value_max}
                                                    onChange={handleChange}
                                                    step={.1}
                                                    min={0.1}
                                                    aria-label="Value 2"
                                                />
                                            </SplitItem>
                                        </>
                                    }

                                </Split>

                                {recommendedSetting.enable_guardrail &&
                                    <div className="pf-u-mb-xs">
                                        {recommendedSetting.guardrail_threshold === "above" &&
                                            <span>Can&#39;t exceed {recommendedSetting.guardrail_value}</span>
                                        }
                                        {recommendedSetting.guardrail_threshold === "below" &&
                                            <span>Can&#39;t go below {recommendedSetting.guardrail_value}</span>
                                        }
                                    </div>
                                }

                                <ActionGroup>
                                    <Button
                                        variant="primary"
                                        onClick={handleSubmit}
                                        spinnerAriaValueText={isFormProcessing ? "Saving" : undefined}
                                        isLoading={isFormProcessing}
                                    >{isFormProcessing ? "Saving" : "Save"}</Button>
                                    <Button variant="link" className="pf-u-pl-0" onClick={() => handleBackButton()}>Back</Button>
                                </ActionGroup>

                                {recommendedSetting.fpu > 0 &&
                                    <>
                                        <Title headingLevel="h2" className="pf-u-mb-0">Recommended Setting</Title>

                                        {recommendedSetting.threshold_range === "above" || recommendedSetting.threshold_range === "below"
                                            ?
                                            <div>
                                                Notify when result is <b>{recommendedSetting.threshold_range === "above" ? "Higher" : "Lower"}</b> Than <b>{recommendedSetting.threshold_value_min}</b>
                                            </div>
                                            :
                                            <div>
                                                Notify when result is <b>{recommendedSetting.threshold_range === "between" ? "Between" : "Outside"}</b> <b>{recommendedSetting.threshold_value_min}</b> And <b>{recommendedSetting.threshold_value_max}</b>
                                            </div>
                                        }

                                    </>
                                }

                            </>

                        }

                    </Form>

                </GridItem>

                <GridItem>
                    {renderChartComponent()}
                </GridItem>

            </Grid>

        </PageSection>

    );

};

export default ChartSettingEdit;